/**
 * Reportes Globales — SuperAdmin
 * Visualización analítica de métricas de la plataforma
 */
import React from 'react';
import { useQuery } from 'react-query';
import { Card, CardContent } from '../../components/ui/card';
import {
  Building2,
  Users,
  TrendingUp,
  PieChart,
  ArrowUpRight,
} from 'lucide-react';
import { adminCompanyApi } from '../../services/companyApi';
import { useAdminCompanies } from '../../hooks/useCompany';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const PLAN_CONFIG = {
  starter: { label: 'Starter', color: 'bg-gray-500', textColor: 'text-gray-700', bgLight: 'bg-gray-100' },
  business: { label: 'Business', color: 'bg-blue-500', textColor: 'text-blue-700', bgLight: 'bg-blue-100' },
  enterprise: { label: 'Enterprise', color: 'bg-purple-500', textColor: 'text-purple-700', bgLight: 'bg-purple-100' },
};

const STATUS_CONFIG = {
  active: { label: 'Activas', color: 'bg-green-500', textColor: 'text-green-700', bgLight: 'bg-green-50' },
  trial: { label: 'Trial', color: 'bg-blue-500', textColor: 'text-blue-700', bgLight: 'bg-blue-50' },
  suspended: { label: 'Suspendidas', color: 'bg-yellow-500', textColor: 'text-yellow-700', bgLight: 'bg-yellow-50' },
  cancelled: { label: 'Canceladas', color: 'bg-red-500', textColor: 'text-red-700', bgLight: 'bg-red-50' },
};

const AdminReports: React.FC = () => {
  const { data: metrics, isLoading: metricsLoading } = useQuery(
    ['admin', 'dashboard', 'metrics'],
    () => adminCompanyApi.getDashboardMetrics(),
    { refetchInterval: 60000 }
  );
  const { data: companies, isLoading: companiesLoading } = useAdminCompanies();

  const isLoading = metricsLoading || companiesLoading;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!metrics) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">Error al cargar los reportes</p>
        </div>
      </div>
    );
  }

  const totalCompanies =
    metrics.companiesByStatus.active +
    metrics.companiesByStatus.trial +
    metrics.companiesByStatus.suspended +
    metrics.companiesByStatus.cancelled;

  const totalUsers =
    metrics.usersByPlan.starter +
    metrics.usersByPlan.business +
    metrics.usersByPlan.enterprise;

  const formatCurrency = (amount: number): string =>
    new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP',
      minimumFractionDigits: 0,
    }).format(amount);

  // Compute plan distribution from companies list
  const planDistribution = companies?.items
    ? {
        starter: companies.items.filter((c) => c.settings.subscriptionPlan === 'starter').length,
        business: companies.items.filter((c) => c.settings.subscriptionPlan === 'business').length,
        enterprise: companies.items.filter((c) => c.settings.subscriptionPlan === 'enterprise').length,
      }
    : { starter: 0, business: 0, enterprise: 0 };

  // Revenue estimate from companies
  const totalMRR = companies?.items
    ?.filter((c) => c.settings.subscriptionStatus === 'active' || c.settings.subscriptionStatus === 'trial')
    .reduce((sum, c) => sum + (c.settings.priceCLP || 0), 0) || 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Reportes Globales</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Análisis y métricas de la plataforma
        </p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="bg-blue-100 rounded-lg p-3">
                <Building2 className="w-6 h-6 text-blue-600" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-muted-foreground">Empresas</p>
                <p className="text-2xl font-bold">{totalCompanies}</p>
                <div className="flex items-center gap-1 mt-1">
                  <ArrowUpRight className="w-3 h-3 text-green-600" />
                  <span className="text-xs text-green-600">+{metrics.newCompaniesLast30Days} últimos 30d</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="bg-purple-100 rounded-lg p-3">
                <Users className="w-6 h-6 text-purple-600" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-muted-foreground">Usuarios</p>
                <p className="text-2xl font-bold">{totalUsers}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {totalCompanies > 0 ? (totalUsers / totalCompanies).toFixed(1) : 0} promedio/empresa
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="bg-green-100 rounded-lg p-3">
                <TrendingUp className="w-6 h-6 text-green-600" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-muted-foreground">MRR Estimado</p>
                <p className="text-2xl font-bold">{formatCurrency(totalMRR)}</p>
                <p className="text-xs text-muted-foreground mt-1">Ingresos mensuales</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="bg-yellow-100 rounded-lg p-3">
                <PieChart className="w-6 h-6 text-yellow-600" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-muted-foreground">Gastos del Mes</p>
                <p className="text-2xl font-bold">{formatCurrency(metrics.totalExpensesThisMonth)}</p>
                <p className="text-xs text-muted-foreground mt-1">{metrics.expenseCountThisMonth} registros</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Distribución por Estado */}
        <Card>
          <CardContent className="p-6">
            <h3 className="text-lg font-semibold mb-4">Distribución por Estado</h3>
            <div className="space-y-4">
              {(Object.entries(STATUS_CONFIG) as [keyof typeof STATUS_CONFIG, typeof STATUS_CONFIG[keyof typeof STATUS_CONFIG]][]).map(
                ([key, config]) => {
                  const count = metrics.companiesByStatus[key];
                  const pct = totalCompanies > 0 ? (count / totalCompanies) * 100 : 0;
                  return (
                    <div key={key}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium text-gray-700">{config.label}</span>
                        <span className="text-sm text-gray-600">
                          {count} ({pct.toFixed(0)}%)
                        </span>
                      </div>
                      <div className="w-full bg-gray-100 rounded-full h-2.5">
                        <div
                          className={`h-2.5 rounded-full ${config.color} transition-all`}
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </div>
                  );
                }
              )}
            </div>
          </CardContent>
        </Card>

        {/* Distribución por Plan */}
        <Card>
          <CardContent className="p-6">
            <h3 className="text-lg font-semibold mb-4">Distribución por Plan</h3>
            <div className="space-y-4">
              {(Object.entries(PLAN_CONFIG) as [keyof typeof PLAN_CONFIG, typeof PLAN_CONFIG[keyof typeof PLAN_CONFIG]][]).map(
                ([key, config]) => {
                  const count = planDistribution[key];
                  const pct = totalCompanies > 0 ? (count / totalCompanies) * 100 : 0;
                  return (
                    <div key={key}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium text-gray-700">{config.label}</span>
                        <span className="text-sm text-gray-600">
                          {count} ({pct.toFixed(0)}%)
                        </span>
                      </div>
                      <div className="w-full bg-gray-100 rounded-full h-2.5">
                        <div
                          className={`h-2.5 rounded-full ${config.color} transition-all`}
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </div>
                  );
                }
              )}
            </div>

            {/* Usuarios por plan */}
            <div className="mt-6 pt-4 border-t">
              <h4 className="text-sm font-semibold text-gray-700 mb-3">Usuarios por Plan</h4>
              <div className="grid grid-cols-3 gap-3">
                {(Object.entries(PLAN_CONFIG) as [keyof typeof PLAN_CONFIG, typeof PLAN_CONFIG[keyof typeof PLAN_CONFIG]][]).map(
                  ([key, config]) => (
                    <div key={key} className={`text-center p-3 rounded-lg ${config.bgLight}`}>
                      <p className={`text-2xl font-bold ${config.textColor}`}>
                        {metrics.usersByPlan[key]}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">{config.label}</p>
                    </div>
                  )
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Ingresos por Plan */}
      {companies?.items && (
        <Card>
          <CardContent className="p-6">
            <h3 className="text-lg font-semibold mb-4">Ingresos Mensuales por Plan</h3>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Plan</th>
                    <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Empresas Activas</th>
                    <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Ingreso Mensual</th>
                    <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">% del MRR</th>
                  </tr>
                </thead>
                <tbody>
                  {(Object.entries(PLAN_CONFIG) as [keyof typeof PLAN_CONFIG, typeof PLAN_CONFIG[keyof typeof PLAN_CONFIG]][]).map(
                    ([key, config]) => {
                      const planCompanies = companies.items.filter(
                        (c) =>
                          c.settings.subscriptionPlan === key &&
                          (c.settings.subscriptionStatus === 'active' || c.settings.subscriptionStatus === 'trial')
                      );
                      const planRevenue = planCompanies.reduce((sum, c) => sum + (c.settings.priceCLP || 0), 0);
                      const pct = totalMRR > 0 ? (planRevenue / totalMRR) * 100 : 0;
                      return (
                        <tr key={key} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-2">
                              <div className={`w-3 h-3 rounded-full ${config.color}`} />
                              <span className="text-sm font-medium text-gray-900">{config.label}</span>
                            </div>
                          </td>
                          <td className="py-3 px-4 text-right text-sm text-gray-700">{planCompanies.length}</td>
                          <td className="py-3 px-4 text-right text-sm font-semibold text-gray-900">
                            {formatCurrency(planRevenue)}
                          </td>
                          <td className="py-3 px-4 text-right text-sm text-gray-600">{pct.toFixed(1)}%</td>
                        </tr>
                      );
                    }
                  )}
                  <tr className="bg-gray-50 font-semibold">
                    <td className="py-3 px-4 text-sm text-gray-900">Total</td>
                    <td className="py-3 px-4 text-right text-sm text-gray-900">
                      {companies.items.filter(
                        (c) => c.settings.subscriptionStatus === 'active' || c.settings.subscriptionStatus === 'trial'
                      ).length}
                    </td>
                    <td className="py-3 px-4 text-right text-sm text-gray-900">{formatCurrency(totalMRR)}</td>
                    <td className="py-3 px-4 text-right text-sm text-gray-900">100%</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Top Empresas por Uso */}
      <Card>
        <CardContent className="p-6">
          <h3 className="text-lg font-semibold mb-4">Top 5 Empresas por Uso</h3>
          <div className="space-y-3">
            {metrics.topCompaniesByUsage.map((company, index) => {
              const maxAmount = metrics.topCompaniesByUsage[0]?.totalAmount || 1;
              const barWidth = (company.totalAmount / maxAmount) * 100;
              return (
                <div key={company.companyId} className="flex items-center gap-4">
                  <div className="flex items-center justify-center w-8 h-8 bg-blue-100 rounded-full flex-shrink-0">
                    <span className="text-sm font-bold text-blue-600">{index + 1}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-sm font-medium text-gray-900 truncate">{company.name}</p>
                      <p className="text-sm font-semibold text-gray-900 ml-2 flex-shrink-0">
                        {formatCurrency(company.totalAmount)}
                      </p>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-2">
                      <div
                        className="h-2 rounded-full bg-blue-500 transition-all"
                        style={{ width: `${barWidth}%` }}
                      />
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">{company.expenseCount} gastos</p>
                  </div>
                </div>
              );
            })}
            {metrics.topCompaniesByUsage.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                No hay datos de uso disponibles
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminReports;
